import { ClientIntakeFormData, FormErrors } from '../types/ClientIntakeForm';
import { getTranslationValue, Locale, translate } from '../i18n';

const getLabels = (locale: Locale, key: string) =>
  getTranslationValue<Record<string, string>>(locale, key);

const formatValue = (
  locale: Locale,
  value: string | null | undefined,
  fallback = translate(locale, 'common.notSpecified')
) => (value && value.trim() ? value : fallback);

const formatList = (
  locale: Locale,
  values: string[],
  labelKey?: string,
  fallback = translate(locale, 'common.notSpecified')
) => {
  if (values.length === 0) {
    return fallback;
  }

  const labels = labelKey ? getLabels(locale, labelKey) : {};
  return values.map((value) => labels[value] ?? value).join(', ');
};

const getLabel = (
  locale: Locale,
  labelKey: string,
  value: string,
  fallback = translate(locale, 'common.notSpecified')
) => {
  const labels = getLabels(locale, labelKey);
  return value ? labels[value] ?? value : fallback;
};

// Validation function for the form
export const validateForm = (
  formData: ClientIntakeFormData,
  locale: Locale
): FormErrors => {
  const errors: FormErrors = {};

  // Required field validations
  if (!formData.firstName.trim()) {
    errors.firstName = translate(locale, 'validation.firstNameRequired');
  }

  if (!formData.lastName.trim()) {
    errors.lastName = translate(locale, 'validation.lastNameRequired');
  }

  if (!formData.email.trim()) {
    errors.email = translate(locale, 'validation.emailRequired');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.email = translate(locale, 'validation.emailInvalid');
  }

  if (!formData.phone.trim()) {
    errors.phone = translate(locale, 'validation.phoneRequired');
  } else if (!/^[+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
    errors.phone = translate(locale, 'validation.phoneInvalid');
  }

  return errors;
};

// Function to format form data for API submission
export const formatFormDataForSubmission = (formData: ClientIntakeFormData) => {
  return {
    // Personal Information
    personalInfo: {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim().toLowerCase(),
      phone: formData.phone.trim(),
    },
    
    // Wedding Details
    weddingDetails: {
      weddingDate: formData.weddingDate || null,
      venue: formData.venue.trim() || null,
      budget: formData.budget || null,
      timeline: formData.timeline || null,
      preference: formData.preference || null,
    },
    
    // Style Preferences
    stylePreferences: {
      dressSilhouette: formData.dressSilhouette,
      preferredColors: formData.preferredColors,
      neckline: formData.neckline || null,
    },
    
    // Additional Information
    additionalInfo: {
      size: formData.size || null,
      specialRequests: formData.specialRequests.trim() || null,
      howDidYouHear: formData.howDidYouHear || null,
    },
    
    // Metadata
    submittedAt: new Date().toISOString(),
    source: 'website_form',
  };
};

// Function to save form data to localStorage (for draft saving)
export const saveFormDraft = (formData: ClientIntakeFormData) => {
  try {
    localStorage.setItem('delyth_form_draft', JSON.stringify(formData));
  } catch (error) {
    console.error('Failed to save form draft:', error);
  }
};

// Function to load form draft from localStorage
export const loadFormDraft = (): ClientIntakeFormData | null => {
  try {
    const draft = localStorage.getItem('delyth_form_draft');
    return draft ? JSON.parse(draft) : null;
  } catch (error) {
    console.error('Failed to load form draft:', error);
    return null;
  }
};

// Function to clear form draft
export const clearFormDraft = () => {
  try {
    localStorage.removeItem('delyth_form_draft');
  } catch (error) {
    console.error('Failed to clear form draft:', error);
  }
};

// Function to generate a summary of the form data
export const generateFormSummary = (
  formData: ClientIntakeFormData,
  locale: Locale
): string => {
  const summary = [
    `${translate(locale, 'email.labels.name')}: ${formData.firstName} ${formData.lastName}`,
    `${translate(locale, 'email.labels.email')}: ${formData.email}`,
    `${translate(locale, 'email.labels.phone')}: ${formData.phone}`,
    `${translate(locale, 'email.labels.weddingDate')}: ${formatValue(locale, formData.weddingDate)}`,
    `${translate(locale, 'email.labels.venue')}: ${formatValue(locale, formData.venue)}`,
    `${translate(locale, 'email.labels.budget')}: ${getLabel(locale, 'formOptions.budget', formData.budget)}`,
    `${translate(locale, 'email.labels.timeline')}: ${getLabel(locale, 'formOptions.timeline', formData.timeline)}`,
    `${translate(locale, 'email.labels.preference')}: ${getLabel(locale, 'formOptions.preference', formData.preference)}`,
    `${translate(locale, 'email.labels.dressSilhouettes')}: ${formatList(locale, formData.dressSilhouette, 'formOptions.dressSilhouettes')}`,
    `${translate(locale, 'email.labels.preferredColors')}: ${formatList(locale, formData.preferredColors, 'formOptions.preferredColors')}`,
    `${translate(locale, 'email.labels.neckline')}: ${getLabel(locale, 'formOptions.neckline', formData.neckline)}`,
    `${translate(locale, 'email.labels.size')}: ${formatValue(locale, formData.size)}`,
    `${translate(locale, 'email.labels.specialRequests')}: ${formatValue(locale, formData.specialRequests)}`,
    `${translate(locale, 'email.labels.howTheyHeard')}: ${getLabel(locale, 'formOptions.howDidYouHear', formData.howDidYouHear)}`,
  ].filter(Boolean);

  return summary.join('\n');
};

export const buildClientIntakeEmailSubject = (
  formData: ClientIntakeFormData,
  locale: Locale
): string => {
  const brideName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
  return brideName
    ? translate(locale, 'email.subjectWithName', { name: brideName })
    : translate(locale, 'email.subjectDefault');
};

export const buildClientIntakeEmailBody = (
  formData: ClientIntakeFormData,
  locale: Locale
): string => {
  const formattedSubmission = formatFormDataForSubmission(formData);

  return [
    translate(locale, 'email.greeting'),
    '',
    translate(locale, 'email.intro'),
    '',
    translate(locale, 'email.sections.clientDetails'),
    `${translate(locale, 'email.labels.name')}: ${formatValue(locale, `${formData.firstName} ${formData.lastName}`.trim())}`,
    `${translate(locale, 'email.labels.email')}: ${formatValue(locale, formData.email)}`,
    `${translate(locale, 'email.labels.phone')}: ${formatValue(locale, formData.phone)}`,
    '',
    translate(locale, 'email.sections.weddingDetails'),
    `${translate(locale, 'email.labels.weddingDate')}: ${formatValue(locale, formData.weddingDate)}`,
    `${translate(locale, 'email.labels.venue')}: ${formatValue(locale, formData.venue)}`,
    `${translate(locale, 'email.labels.budgetRange')}: ${getLabel(locale, 'formOptions.budget', formData.budget)}`,
    `${translate(locale, 'email.labels.timeline')}: ${getLabel(locale, 'formOptions.timeline', formData.timeline)}`,
    `${translate(locale, 'email.labels.preference')}: ${getLabel(locale, 'formOptions.preference', formData.preference)}`,
    '',
    translate(locale, 'email.sections.stylePreferences'),
    `${translate(locale, 'email.labels.dressSilhouette')}: ${formatList(locale, formData.dressSilhouette, 'formOptions.dressSilhouettes')}`,
    `${translate(locale, 'email.labels.preferredColors')}: ${formatList(locale, formData.preferredColors, 'formOptions.preferredColors')}`,
    `${translate(locale, 'email.labels.preferredNeckline')}: ${getLabel(locale, 'formOptions.neckline', formData.neckline)}`,
    '',
    translate(locale, 'email.sections.additionalNotes'),
    `${translate(locale, 'email.labels.currentDressSize')}: ${formatValue(locale, formData.size)}`,
    `${translate(locale, 'email.labels.specialRequests')}: ${formatValue(locale, formData.specialRequests)}`,
    `${translate(locale, 'email.labels.howSheHeard')}: ${getLabel(locale, 'formOptions.howDidYouHear', formData.howDidYouHear)}`,
    '',
    translate(locale, 'email.sections.systemDetails'),
    `${translate(locale, 'email.labels.submittedAt')}: ${formattedSubmission.submittedAt}`,
    `${translate(locale, 'email.labels.source')}: ${formattedSubmission.source}`,
  ].join('\n');
};

export const buildClientIntakeMailtoLink = (
  formData: ClientIntakeFormData,
  recipientEmail: string,
  locale: Locale
): string => {
  const subject = encodeURIComponent(buildClientIntakeEmailSubject(formData, locale));
  const body = encodeURIComponent(buildClientIntakeEmailBody(formData, locale));

  return `mailto:${recipientEmail}?subject=${subject}&body=${body}`;
};

























